// ⚠️ PERFORM CORE — Ne pas modifier sans validation complète
// Connecté à : program_checkins
// Utilisé dans : Dashboard, DailyProgressionCards, BrainAlerts
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { PlanningBlock, DayKey } from "@/types/planning";
import { DAYS } from "@/types/planning";
import type { OverlayBlock } from "@/hooks/useWeeklyPlanOverlay";

export interface DayCheckin {
  id: string;
  checkin_date: string;
  training_done: boolean;
  nutrition_done: boolean;
  prevention_done: boolean;
  sleep_hours: number | null;
  pain_flags: { zone: string; intensity: number }[];
  notes: string | null;
  completed_blocks: string[];
}

export interface DayProgression {
  dayKey: DayKey;
  label: string;
  shortLabel: string;
  date: string;
  isToday: boolean;
  isPast: boolean;
  /** Planned training session from overlay (if any) */
  plannedSession: OverlayBlock | null;
  /** All overlay blocks for this day (training, nutrition, recovery…) */
  allBlocks: OverlayBlock[];
  /** Check-in data for this day */
  checkin: DayCheckin | null;
}

function getWeekDates(): { dayKey: DayKey; date: string }[] {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));

  return DAYS.map((d, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return { dayKey: d.key, date: date.toISOString().split("T")[0] };
  });
}

export function useDailyProgression(
  overlayBlocks: OverlayBlock[],
  programId: string | null
) {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState<DayCheckin[]>([]);
  const checkinsRef = useRef(checkins);
  checkinsRef.current = checkins;
  const [loading, setLoading] = useState(true);

  const weekDates = useMemo(() => getWeekDates(), []);
  const todayStr = new Date().toISOString().split("T")[0];

  const fetchCheckins = useCallback(async () => {
    if (!user || weekDates.length === 0) {
      setLoading(false);
      return;
    }

    const startDate = weekDates[0].date;
    const endDate = weekDates[weekDates.length - 1].date;

    try {
      const { data, error } = await supabase
        .from("program_checkins")
        .select("*")
        .eq("user_id", user.id)
        .gte("checkin_date", startDate)
        .lte("checkin_date", endDate);

      if (error) {
        console.error("[useDailyProgression] fetch error", error);
      } else if (data) {
        setCheckins(
          (data as any[]).map((r) => ({
            id: r.id,
            checkin_date: r.checkin_date,
            training_done: r.training_done,
            nutrition_done: r.nutrition_done,
            prevention_done: r.prevention_done,
            sleep_hours: r.sleep_hours,
            pain_flags: (r.pain_flags as any[]) || [],
            notes: r.notes,
            completed_blocks: (r.completed_blocks as string[]) || [],
          }))
        );
      }
    } catch (err) {
      console.error("[useDailyProgression] unexpected error", err);
    } finally {
      setLoading(false);
    }
  }, [user, weekDates]);

  useEffect(() => {
    fetchCheckins();
  }, [fetchCheckins]);

  const days: DayProgression[] = useMemo(() => {
    return weekDates.map(({ dayKey, date }) => {
      const dayLabel = DAYS.find((d) => d.key === dayKey);
      const checkin = checkins.find((c) => c.checkin_date === date) || null;
      const dayBlocks = overlayBlocks.filter((b) => b.day === dayKey);
      const plannedSession =
        dayBlocks.find((b) => b.category === "training") || null;

      return {
        dayKey,
        label: dayLabel?.label || dayKey,
        shortLabel: dayLabel?.short || dayKey,
        date,
        isToday: date === todayStr,
        isPast: date < todayStr,
        plannedSession,
        allBlocks: dayBlocks,
        checkin,
      };
    });
  }, [weekDates, checkins, overlayBlocks, todayStr]);

  // Quick‐save a field with optimistic update
  const quickUpdate = useCallback(
    async (
      date: string,
      field: "training_done" | "nutrition_done" | "prevention_done" | "sleep_hours",
      value: boolean | number
    ) => {
      if (!user) return;

      // Optimistic update
      setCheckins((prev) => {
        const existing = prev.find((c) => c.checkin_date === date);
        if (existing) {
          return prev.map((c) =>
            c.checkin_date === date ? { ...c, [field]: value } : c
          );
        }
        return [
          ...prev,
          {
            id: `temp-${date}`,
            checkin_date: date,
            training_done: false,
            nutrition_done: false,
            prevention_done: false,
            sleep_hours: null,
            pain_flags: [],
            notes: null,
            completed_blocks: [],
            [field]: value,
          } as DayCheckin,
        ];
      });

      const existing = checkinsRef.current.find((c) => c.checkin_date === date);
      if (existing && !existing.id.startsWith("temp-")) {
        await supabase
          .from("program_checkins")
          .update({ [field]: value } as any)
          .eq("id", existing.id);
      } else {
        const { data } = await supabase
          .from("program_checkins")
          .insert({
            user_id: user.id,
            program_id: programId,
            checkin_date: date,
            [field]: value,
          } as any)
          .select()
          .single();

        // Replace temp id with real id
        if (data) {
          setCheckins((prev) =>
            prev.map((c) =>
              c.checkin_date === date && c.id.startsWith("temp-")
                ? { ...c, id: (data as any).id }
                : c
            )
          );
        }
      }
    },
    [user, programId]
  );

  // Toggle a specific block's completion status
  const quickToggleBlock = useCallback(
    async (date: string, blockId: string, trainingBlockIds: string[]) => {
      if (!user) return;

      const current = checkinsRef.current.find((c) => c.checkin_date === date);
      const currentBlocks = current?.completed_blocks || [];
      const isCompleted = currentBlocks.includes(blockId);
      const newBlocks = isCompleted
        ? currentBlocks.filter((b) => b !== blockId)
        : [...currentBlocks, blockId];

      // training_done = all training blocks completed
      const allTrainingDone = trainingBlockIds.every((id) => newBlocks.includes(id));

      // Optimistic update
      setCheckins((prev) => {
        const existing = prev.find((c) => c.checkin_date === date);
        if (existing) {
          return prev.map((c) =>
            c.checkin_date === date
              ? { ...c, completed_blocks: newBlocks, training_done: allTrainingDone }
              : c
          );
        }
        return [
          ...prev,
          {
            id: `temp-${date}`,
            checkin_date: date,
            training_done: allTrainingDone,
            nutrition_done: false,
            prevention_done: false,
            sleep_hours: null,
            pain_flags: [],
            notes: null,
            completed_blocks: newBlocks,
          } as DayCheckin,
        ];
      });

      // ✅ Utiliser `current` capturé AVANT le setState optimiste
      if (current && !current.id.startsWith("temp-")) {
        const { error } = await supabase
          .from("program_checkins")
          .update({ completed_blocks: newBlocks, training_done: allTrainingDone } as any)
          .eq("id", current.id);
        if (error) {
          console.error("[quickToggleBlock] update error:", error);
          // Rollback optimistic update
          setCheckins((prev) =>
            prev.map((c) =>
              c.checkin_date === date ? { ...c, completed_blocks: currentBlocks, training_done: !allTrainingDone } : c
            )
          );
        }
      } else {
        const { data, error } = await supabase
          .from("program_checkins")
          .insert({
            user_id: user.id,
            program_id: programId,
            checkin_date: date,
            completed_blocks: newBlocks,
            training_done: allTrainingDone,
          } as any)
          .select()
          .single();

        if (error) {
          console.error("[quickToggleBlock] insert error:", error);
          // Rollback optimistic update
          setCheckins((prev) => prev.filter((c) => c.checkin_date !== date || !c.id.startsWith("temp-")));
        } else if (data) {
          setCheckins((prev) =>
            prev.map((c) =>
              c.checkin_date === date && c.id.startsWith("temp-")
                ? { ...c, id: (data as any).id }
                : c
            )
          );
        }
      }
    },
    [user, programId]
  );

  return { days, loading, refetch: fetchCheckins, quickUpdate, quickToggleBlock };
}
